import {
  WEEKDAY_PLURAL,
  WEEKDAY_SHORT,
  closureChanges,
  formatDateKey,
  itemChanges,
  listWords,
  weekdayOf,
  type DraftItem,
  type ItemChange,
  type MovedDay,
  type PublishTimetableResult,
  type TimetableDraft,
} from "@teamna/shared";
import { Button } from "../../components/index.ts";
import type { RailWords } from "./Rail.tsx";
import type { BoardGroup } from "./WeekBoard.tsx";
import { bookedOnDate, bookedTotal, sessionsChange, timeLabel, type BookedIndex, type EditorProblem } from "./model.ts";

/**
 * Review and publish: every change in plain words, what it does to booked people (the server's dry
 * run of this exact draft), the problems that stop it, and the one message each family gets.
 * Nothing changes for families until Publish, and Publish is all or nothing.
 */

type Line = { color: string | null; title: string; detail: string; message?: string; bad?: boolean };

function days(item: DraftItem): string {
  return listWords([...item.days].sort((a, b) => a - b).map((day) => WEEKDAY_SHORT[day]!));
}

function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

export function reviewLines(input: {
  base: TimetableDraft;
  draft: TimetableDraft;
  groups: Map<string, BoardGroup>;
  booked: BookedIndex;
  personName: (userId: string) => string;
  businessName: string;
  sessionsOf: (item: DraftItem) => number;
  untilLabel: (until: string | null) => string;
  words: RailWords;
}): { term: Line[]; added: Line[]; changed: Line[]; removed: Line[]; dates: Line[] } {
  const { base, draft, groups, booked, personName } = input;
  // "Wednesdays: Rami is now Hala"
  const movedWords = (moved: MovedDay[], to: string | null) =>
    moved
      .map(
        (row) =>
          `${WEEKDAY_PLURAL[row.day]}: ${row.fromStaff ? personName(row.fromStaff) : `no ${input.words.staff}`} is now ${to ? personName(to) : `no ${input.words.staff}`}`,
      )
      .join(", ");
  const name = (item: DraftItem) =>
    item.free ? `${item.staffUserId ? personName(item.staffUserId) : "No one yet"}, private hours ${timeLabel(item.start)} to ${timeLabel(item.end)}` : `${groups.get(item.groupId)?.name ?? "Level"} at ${timeLabel(item.start)}`;
  const color = (item: DraftItem) => groups.get(item.groupId)?.color ?? null;
  const groupName = (item: DraftItem) => groups.get(item.groupId)?.name ?? "Level";

  const out = { term: [] as Line[], added: [] as Line[], changed: [] as Line[], removed: [] as Line[], dates: [] as Line[] };

  if (base.until !== draft.until) {
    out.term.push({
      color: null,
      title: `${draft.until === null ? "No end date" : `Runs until ${input.untilLabel(draft.until)}`}, not ${base.until === null ? "no end date" : input.untilLabel(base.until)}`,
      detail: draft.until === null ? "Teamna makes sessions 8 weeks ahead each night." : "Every weekly time ends on this date.",
    });
  }

  for (const change of itemChanges(base, draft) as ItemChange[]) {
    if (change.kind === "added") {
      const item = change.item;
      out.added.push({
        color: color(item),
        title: `${name(item)}, ${days(item)}`,
        detail: item.free
          ? `${input.words.families.charAt(0).toUpperCase()}${input.words.families.slice(1)} can book private sessions in these hours.`
          : `${plural(input.sessionsOf(item), "session", "sessions")}. ${item.capacity === null ? "No limit" : plural(item.capacity, "place", "places")}, ${item.staffUserId ? personName(item.staffUserId) : "no one yet"}.`,
      });
    } else if (change.kind === "split") {
      // Days that went to another coach with their rows: same sessions, same bookings.
      const item = change.item;
      out.changed.push({
        color: color(item),
        title: `${name(item)}, ${days(item)}`,
        detail: change.daysAdded.length
          ? `${movedWords(change.daysMovedIn, item.staffUserId)}, adds ${listWords(change.daysAdded.map((day) => WEEKDAY_PLURAL[day]!))}. Bookings on the moved days stay.`
          : `${movedWords(change.daysMovedIn, item.staffUserId)}. No booking changes.`,
      });
    } else if (change.kind === "removed") {
      const item = change.item;
      const count = bookedTotal(item, booked);
      out.removed.push({
        color: color(item),
        title: `${name(item)}, ${days(item)}`,
        detail: `${item.free ? "No more private sessions in these hours." : "Its sessions come off."}${count ? ` ${plural(count, "booking gets", "bookings get")} the session back.` : ""}`,
        message: count
          ? item.free
            ? `Private sessions on ${listWords(item.days.map((day) => WEEKDAY_PLURAL[day]!))} at ${timeLabel(item.start)} stop.`
            : `${groupName(item)} on ${listWords(item.days.map((day) => WEEKDAY_PLURAL[day]!))} at ${timeLabel(item.start)} stops.`
          : undefined,
      });
    } else {
      const { before, item } = change;
      const parts: string[] = [];
      if (change.time) parts.push(item.free ? `${timeLabel(before.start)} to ${timeLabel(before.end)} is now ${timeLabel(item.start)} to ${timeLabel(item.end)}` : `${timeLabel(before.start)} is now ${timeLabel(item.start)}`);
      if (change.length) parts.push(`${before.end - before.start} minutes is now ${item.end - item.start}`);
      if (change.capacity) parts.push(`${before.capacity === null ? "no limit" : plural(before.capacity, "place", "places")} is now ${item.capacity === null ? "no limit" : plural(item.capacity, "place", "places")}`);
      if (change.person) {
        parts.push(
          before.staffUserId === null
            ? `the ${input.words.staff} is now ${personName(item.staffUserId!)}`
            : item.staffUserId === null
              ? `no ${input.words.staff} now, not ${personName(before.staffUserId)}`
              : `${personName(before.staffUserId)} is now ${personName(item.staffUserId)}`,
        );
      }
      if (change.name) parts.push(item.name ? `the session name is now "${item.name}"` : "the session has no name now");
      if (change.detail) parts.push(item.detail ? `what it is: "${item.detail}"` : "no line under the name now");
      if (change.daysAdded.length) parts.push(`adds ${listWords(change.daysAdded.map((day) => WEEKDAY_PLURAL[day]!))}`);
      if (change.daysMovedIn.length) parts.push(movedWords(change.daysMovedIn, item.staffUserId));
      if (change.daysRemoved.length) parts.push(`stops on ${listWords(change.daysRemoved.map((day) => WEEKDAY_PLURAL[day]!))}`);
      const kept = before.days.filter((day) => item.days.includes(day));
      const moved = !item.free && change.time ? bookedTotal(before, booked, kept) : 0;
      const back = bookedTotal(before, booked, change.daysRemoved);
      const effects: string[] = [];
      if (moved) effects.push(`${plural(moved, "booking moves", "bookings move")} with it.`);
      if (back) effects.push(`${plural(back, "booking gets", "bookings get")} the session back.`);
      if (item.free && change.time) effects.push("Booked private sessions inside the new hours stay. Any outside get their session back.");
      if (effects.length === 0) effects.push("No booking changes.");
      const sentence = parts.join(", ");
      out.changed.push({
        color: color(item),
        title: `${name(before)}, ${days(before)}`,
        detail: `${sentence.charAt(0).toUpperCase()}${sentence.slice(1)}. ${effects.join(" ")}`,
        message: moved
          ? `${groupName(item)} on ${listWords(kept.map((day) => WEEKDAY_PLURAL[day]!))} is now at ${timeLabel(item.start)}, not ${timeLabel(before.start)}.`
          : undefined,
      });
    }
  }

  const closures = closureChanges(base, draft);
  for (const closure of closures.added) {
    const weekday = weekdayOf(closure.date);
    const touched = closure.item === null ? draft.items.filter((item) => item.days.includes(weekday)) : draft.items.filter((item) => item.key === closure.item);
    const count = touched.reduce((sum, item) => sum + bookedOnDate(item, closure.date, weekday, booked), 0);
    const sessions = touched.filter((item) => !item.free).length;
    if (closure.item === null) {
      out.dates.push({
        color: null,
        title: `Closed on ${formatDateKey(closure.date)}${closure.reason ? ` (${closure.reason})` : ""}`,
        detail: `${plural(sessions, "session comes", "sessions come")} off.${count ? ` ${plural(count, "booking gets", "bookings get")} the session back.` : ""}`,
        message: count ? `${input.businessName} is closed on ${formatDateKey(closure.date)}${closure.reason ? ` (${closure.reason})` : ""}.` : undefined,
      });
    } else {
      const item = touched[0];
      if (!item) continue;
      out.dates.push({
        color: color(item),
        title: `${name(item)} on ${formatDateKey(closure.date)} is off`,
        detail: count ? `${plural(count, "booking gets", "bookings get")} the session back.` : "No one is booked.",
        message: count ? `${groupName(item)} on ${formatDateKey(closure.date)} is off.` : undefined,
      });
    }
  }
  for (const closure of closures.removed) {
    const item = closure.item ? base.items.find((row) => row.key === closure.item) : null;
    out.dates.push({
      color: item ? color(item) : null,
      title: item ? `${name(item)} on ${formatDateKey(closure.date)} is on again` : `Open again on ${formatDateKey(closure.date)}`,
      detail: "The sessions come back. Bookings that were taken off stay off.",
    });
  }
  return out;
}

export function ReviewPanel({
  lines,
  problems,
  preview,
  previewLoading,
  sessionsDelta,
  sessionCounts,
  businessName,
  publishing,
  published,
  words,
  onBack,
  onPublish,
}: {
  lines: ReturnType<typeof reviewLines>;
  problems: EditorProblem[];
  preview: PublishTimetableResult | undefined;
  previewLoading: boolean;
  sessionsDelta: number;
  /** Class sessions from today, now and after the publish, each to its own end of term (as words). */
  /** `from`: "today", or the first day of a month that starts later ("Sun 1 Nov"). */
  sessionCounts: { from: string; now: number; nowUntil: string; after: number; afterUntil: string };
  businessName: string;
  publishing: boolean;
  words: RailWords;
  /** Set after a publish: the done view. */
  published: { summary: PublishTimetableResult["summary"]; sessions: number; until: string } | null;
  onBack: () => void;
  onPublish: () => void;
}) {
  const Families = `${words.families.charAt(0).toUpperCase()}${words.families.slice(1)}`;
  if (published) {
    const { summary } = published;
    return (
      <div className="grid content-start justify-items-center gap-2 px-5 py-10 text-center">
        <svg viewBox="0 0 64 64" className="size-16" aria-hidden="true">
          <circle cx="32" cy="32" r="32" fill="var(--color-confirmed)" />
          <path d="M20 33l8 8 16-17" fill="none" stroke="#fff" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <h2 className="mt-1 text-[21px] font-bold">Published</h2>
        <p className="max-w-[36ch] text-[13px] text-ink-2">
          {plural(published.sessions, "session is", "sessions are")} on the schedule until {published.until}. {Families} can book the new times now.
        </p>
        {summary.parentsTold ? (
          <p className="max-w-[36ch] text-[13px] text-ink-2">{plural(summary.parentsTold, `${words.family} got`, `${words.families} got`)} one message each.</p>
        ) : null}
        <div className="mt-3">
          <Button onClick={onBack}>Back to the schedule</Button>
        </div>
      </div>
    );
  }

  const count = lines.term.length + lines.added.length + lines.changed.length + lines.removed.length + lines.dates.length;
  const messages = [...lines.changed, ...lines.dates, ...lines.removed]
    .map((line) => line.message)
    .filter((message): message is string => Boolean(message))
    .slice(0, 2);
  const groupsOf: Array<[string, Line[]]> = [
    ["End of the term", lines.term],
    ["New every week", lines.added],
    ["Changed", lines.changed],
    ["Off the schedule", lines.removed],
    ["Dates", lines.dates],
  ];

  return (
    <div className="grid h-full grid-rows-[auto_minmax(0,1fr)_auto]">
      <div className="grid gap-1 border-b border-line px-5 pt-4 pb-3">
        <h2 className="text-[18px] font-bold">Review and publish</h2>
        <p className="text-[12.5px] text-ink-2">
          {plural(count, "change", "changes")}. Nothing changes for {words.families} until you publish. Teamna publishes all of them or none.
        </p>
      </div>
      <div className="grid content-start gap-2 overflow-y-auto px-5 py-3">
        {problems.length ? (
          <>
            <p className="pt-1 text-[11.5px] font-bold tracking-[0.04em] text-cancelled uppercase">Fix first</p>
            {problems.map((problem) => (
              <div key={`${problem.code}-${problem.slots.join(",")}`} className="rounded-xl border border-cancelled/40 bg-cancelled-surface px-3 py-2 text-[12.5px] font-semibold text-cancelled">
                {problem.detail}
              </div>
            ))}
          </>
        ) : null}
        {groupsOf.map(([title, rows]) =>
          rows.length ? (
            <section key={title} aria-label={title} className="grid gap-2">
              <p className="pt-1 text-[11.5px] font-bold tracking-[0.04em] text-ink-3 uppercase">{title}</p>
              {rows.map((line) => (
                <div key={line.title} className="flex gap-2.5 rounded-xl border border-line px-2.5 py-2">
                  <i className="w-2 shrink-0 rounded" style={{ background: line.color ?? "var(--color-line-strong)" }} aria-hidden="true" />
                  <span className="grid min-w-0 gap-0.5 text-[12px] text-ink-2">
                    <b className="text-[13.5px] text-ink">{line.title}</b>
                    {line.detail}
                  </span>
                </div>
              ))}
            </section>
          ) : null,
        )}
        {messages.length ? (
          <section aria-label={`The message ${words.families} get`} className="grid gap-1.5">
            <p className="pt-1 text-[11.5px] font-bold tracking-[0.04em] text-ink-3 uppercase">The message {words.families} get</p>
            <div className="grid gap-1 rounded-[12px_12px_12px_4px] bg-sunken px-3 py-2 text-[12.5px] text-ink">
              <span className="text-[10.5px] font-bold text-tile-came-ink">{businessName}</span>
              {messages.join(" ")} Open Teamna to see your dates.
            </div>
            <p className="text-[12px] text-ink-3">Each {words.family} gets one message with only their own changes.</p>
          </section>
        ) : null}
      </div>
      <div className="grid gap-2 border-t border-line px-5 pt-3 pb-4">
        <div className="grid grid-cols-3 gap-1.5" aria-busy={previewLoading}>
          {[
            [String(sessionsChange(sessionsDelta).value), sessionsChange(sessionsDelta).label],
            [preview ? String(preview.summary.bookingsMoved + preview.summary.bookingsReturned) : "…", "bookings change"],
            [preview ? String(preview.summary.parentsTold) : "…", `${words.families} told`],
          ].map(([value, label]) => (
            <div key={label} className="grid rounded-[10px] bg-sunken px-2.5 py-1.5 text-[11px] text-ink-2">
              <b className="text-[18px] font-extrabold text-ink tabular-nums">{value}</b>
              {label}
            </div>
          ))}
        </div>
        {sessionsDelta ? (
          <p className="text-[11.5px] text-ink-3">
            After you publish: {plural(sessionCounts.after, "session", "sessions")} from {sessionCounts.from} to {sessionCounts.afterUntil}. Now:{" "}
            {plural(sessionCounts.now, "session", "sessions")} to {sessionCounts.nowUntil}.
          </p>
        ) : null}
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onBack}>
            Back to editing
          </Button>
          <Button onClick={onPublish} loading={publishing} disabled={problems.length > 0 || count === 0}>
            {problems.length > 0 ? "Fix the problems first" : "Publish"}
          </Button>
        </div>
      </div>
    </div>
  );
}
